/**
 * Just-for-fun fireworks overlay. `launchFireworks(origin)` drops a full-screen,
 * click-through canvas on document.body (so it escapes the app's transformed
 * root), fires a volley of shells up and out from `origin`, bursts them into
 * fading sparks, and cleans itself up when the show ends. Sound is synthesized
 * with the Web Audio API (no asset files, CSP-safe). Everything is optional and
 * gated by a caller-supplied on/off flag.
 */
export interface FireworksOptions {
  sound?: boolean
  durationMs?: number
}

type AudioKit = { launch: () => void; boom: () => void }

// One shared, lazily-created AudioContext. Browsers start it suspended until a
// user gesture, so we resume on creation (called right after the tab click) and
// again on the next pointer/key event as a fallback.
let sharedCtx: AudioContext | null = null
function audioCtx(): AudioContext | null {
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  if (!sharedCtx) {
    sharedCtx = new Ctor()
    const resume = () => sharedCtx?.state === "suspended" && void sharedCtx.resume().catch(() => {})
    window.addEventListener("pointerdown", resume, { once: false })
    window.addEventListener("keydown", resume, { once: false })
  }
  if (sharedCtx.state === "suspended") void sharedCtx.resume().catch(() => {})
  return sharedCtx
}

/** Warm up / unlock audio during a user gesture (e.g. opening the tab). */
export function unlockAudio(): void {
  audioCtx()
}

function makeAudio(): AudioKit | null {
  const ctx = audioCtx()
  if (!ctx) return null
  const launch = () => {
    const t = ctx.currentTime
    const o = ctx.createOscillator()
    const g = ctx.createGain()
    o.type = "sine"
    o.frequency.setValueAtTime(480, t)
    o.frequency.exponentialRampToValueAtTime(1500, t + 0.5)
    g.gain.setValueAtTime(0.0001, t)
    g.gain.exponentialRampToValueAtTime(0.05, t + 0.05)
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.6)
    o.connect(g).connect(ctx.destination)
    o.start(t)
    o.stop(t + 0.62)
  }
  const boom = () => {
    const t = ctx.currentTime
    // low thump
    const o = ctx.createOscillator()
    const g = ctx.createGain()
    o.type = "sine"
    o.frequency.setValueAtTime(130, t)
    o.frequency.exponentialRampToValueAtTime(38, t + 0.4)
    g.gain.setValueAtTime(0.3, t)
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.5)
    o.connect(g).connect(ctx.destination)
    o.start(t)
    o.stop(t + 0.5)
    // crackle: a short filtered noise burst
    const dur = 0.5
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate)
    const d = buf.getChannelData(0)
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length) ** 2
    const src = ctx.createBufferSource()
    src.buffer = buf
    const bp = ctx.createBiquadFilter()
    bp.type = "bandpass"
    bp.frequency.value = 1100
    const g2 = ctx.createGain()
    g2.gain.setValueAtTime(0.22, t)
    g2.gain.exponentialRampToValueAtTime(0.001, t + dur)
    src.connect(bp).connect(g2).connect(ctx.destination)
    src.start(t)
    src.stop(t + dur)
  }
  return { launch, boom }
}

interface Shell {
  x: number
  y: number
  vx: number
  vy: number
  hue: number
  trail: { x: number; y: number }[] // recent head positions → comet streak
  smokeTick: number
}
interface Spark {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  max: number
  hue: number
  size: number
}
interface Smoke {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  max: number
  r: number
  grow: number
}

const rand = (a: number, b: number) => a + Math.random() * (b - a)
const GRAVITY = 0.055 // gentle → slow, graceful arcs

export function launchFireworks(origin: { x: number; y: number }, opts: FireworksOptions = {}): () => void {
  const { sound = true, durationMs = 15000 } = opts
  const canvas = document.createElement("canvas")
  canvas.style.cssText =
    "position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:2147483000"
  document.body.appendChild(canvas)
  const ctx = canvas.getContext("2d")
  if (!ctx) {
    canvas.remove()
    return () => {}
  }
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const resize = () => {
    canvas.width = Math.floor(window.innerWidth * dpr)
    canvas.height = Math.floor(window.innerHeight * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }
  resize()
  window.addEventListener("resize", resize)

  const audio = sound ? makeAudio() : null
  const shellArr: Shell[] = []
  const sparks: Spark[] = []
  const smoke: Smoke[] = []
  const W = () => window.innerWidth
  const H = () => window.innerHeight

  const puffSmoke = (x: number, y: number, big = false) =>
    smoke.push({
      x: x + rand(-3, 3),
      y: y + rand(-3, 3),
      vx: rand(-0.25, 0.25),
      vy: -rand(0.15, 0.5), // drifts up
      life: 0,
      max: rand(55, 100),
      r: big ? rand(6, 10) : rand(2.5, 5),
      grow: rand(0.25, 0.55),
    })

  const spawnShell = () => {
    // A gentle, clearly-visible launch straight-ish up and out from the row.
    for (let k = 0; k < 8; k++) puffSmoke(origin.x, origin.y, true) // launch smoke at the base
    shellArr.push({
      x: origin.x,
      y: origin.y,
      vx: rand(-1, 1) * rand(1.3, 4.2), // modest spread → travels across the screen
      vy: -rand(6, 8.6), // slow rise
      hue: Math.floor(rand(0, 360)),
      trail: [],
      smokeTick: 0,
    })
    audio?.launch()
  }
  const explode = (s: Shell) => {
    const n = Math.floor(rand(60, 100))
    for (let i = 0; i < n; i++) {
      const a = rand(0, Math.PI * 2)
      const sp = rand(0.5, 4.4) // slower burst
      sparks.push({
        x: s.x,
        y: s.y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        life: 0,
        max: rand(70, 130), // linger longer as they fall
        hue: s.hue + rand(-18, 18),
        size: rand(1.3, 2.8),
      })
    }
    for (let k = 0; k < 5; k++) puffSmoke(s.x, s.y) // burst smoke
    audio?.boom()
  }

  let stopped = false
  let raf = 0
  const start = performance.now()
  let nextAt = 400 // first shell ~0.4s in
  const spawnUntil = durationMs - 3200 // keep launching until ~3s before the end
  const cleanup = () => {
    if (stopped) return
    stopped = true
    cancelAnimationFrame(raf)
    window.removeEventListener("resize", resize)
    canvas.remove()
  }

  const frame = (now: number) => {
    if (stopped) return
    const t = now - start
    if (t < spawnUntil && t >= nextAt) {
      spawnShell()
      nextAt = t + rand(500, 1050) // relaxed cadence
    }

    ctx.clearRect(0, 0, W(), H())

    // --- smoke (soft grey, behind everything) ---
    ctx.globalCompositeOperation = "source-over"
    for (let i = smoke.length - 1; i >= 0; i--) {
      const p = smoke[i]
      p.life++
      p.vy *= 0.99
      p.x += p.vx
      p.y += p.vy
      p.r += p.grow
      const a = (1 - p.life / p.max) * 0.16 // "light" smoke
      if (a <= 0) {
        smoke.splice(i, 1)
        continue
      }
      ctx.globalAlpha = a
      ctx.beginPath()
      ctx.fillStyle = "hsl(230,12%,62%)"
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.globalAlpha = 1

    // --- shells + comet trails, then explosion sparks (additive glow) ---
    ctx.globalCompositeOperation = "lighter"
    for (let i = shellArr.length - 1; i >= 0; i--) {
      const s = shellArr[i]
      s.vy += GRAVITY
      s.x += s.vx
      s.y += s.vy
      s.trail.push({ x: s.x, y: s.y })
      if (s.trail.length > 12) s.trail.shift()
      if (s.smokeTick++ % 2 === 0) puffSmoke(s.x, s.y) // smoke trail while rising
      // comet streak
      for (let j = 0; j < s.trail.length; j++) {
        const p = s.trail[j]
        const a = j / s.trail.length
        ctx.globalAlpha = a * 0.6
        ctx.beginPath()
        ctx.fillStyle = `hsl(${s.hue},90%,70%)`
        ctx.arc(p.x, p.y, a * 1.8 + 0.4, 0, Math.PI * 2)
        ctx.fill()
      }
      // bright head
      ctx.globalAlpha = 1
      ctx.beginPath()
      ctx.fillStyle = `hsl(${s.hue},100%,82%)`
      ctx.arc(s.x, s.y, 2.4, 0, Math.PI * 2)
      ctx.fill()
      if (s.vy >= 0) {
        explode(s)
        shellArr.splice(i, 1)
      }
    }

    for (let i = sparks.length - 1; i >= 0; i--) {
      const p = sparks[i]
      p.life++
      p.vy += GRAVITY * 0.6
      p.vx *= 0.99
      p.vy *= 0.99
      p.x += p.vx
      p.y += p.vy
      const a = 1 - p.life / p.max
      if (a <= 0) {
        sparks.splice(i, 1)
        continue
      }
      ctx.globalAlpha = a
      ctx.beginPath()
      ctx.fillStyle = `hsl(${p.hue},100%,${55 + a * 25}%)`
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.globalAlpha = 1

    const settled = shellArr.length === 0 && sparks.length === 0 && smoke.length === 0
    if ((t >= spawnUntil && settled) || t > durationMs + 4000) cleanup()
    else raf = requestAnimationFrame(frame)
  }
  raf = requestAnimationFrame(frame)

  return cleanup
}
